import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useIsClient, statusLabel } from "@/components/common";
import {
  AlertTriangle, Clock, Calendar, CheckCircle2, ChevronDown, ChevronRight,
  Plus, Pencil, MoreVertical, Users, UserPlus, Activity as ActivityIcon, Send, X,
  Trash2, RotateCcw, ListChecks,
} from "lucide-react";

// ---- exact tokens from the approved concept ----
export const TOK = {
  border: "#E7E8F2", card: "#FFFFFF", categoryBg: "#FBFBFE",
  indigo: "#5850EC", track: "#EEEDF9", ink: "#1E2033", inkSoft: "#6B6E85", inkFaint: "#9A9DB0",
};
const STATUS = {
  "Received": { bg: "#F1F2F7", fg: "#5B5E70", dot: "#9A9DB0" },
  "Awaiting Client": { bg: "#EAF3FF", fg: "#2564C6", dot: "#3B82F6" },
  "Submitted": { bg: "#F1EFFE", fg: "#5850EC", dot: "#5850EC" },
  "Complete": { bg: "#E9F9F0", fg: "#1E8E5A", dot: "#22B573" },
  "Overdue": { bg: "#FDECEC", fg: "#C0392B", dot: "#E5484D" },
};
const TILE_TINT = {
  overdue: { bg: "#FDECEC", fg: "#C0392B" }, dueThisWeek: { bg: "#FFF6E6", fg: "#B7791F" },
  upcoming: { bg: "#EAF3FF", fg: "#2564C6" }, completed: { bg: "#F1EFFE", fg: "#5850EC" },
};
const DUE_LABEL = { overdue: "Overdue", week: "This week", next: "Next week", later: "Later" };
const DUE_COLOR = { overdue: "#E5484D", week: "#5850EC", next: "#B7791F", later: "#2564C6" };

const card = "rounded-[14px] bg-white";
const cardBorder = { border: `1px solid ${TOK.border}` };

function initials(n) { return (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }

export function StatusPill({ status }) {
  const isClient = useIsClient();
  const s = STATUS[status] || STATUS.Received;
  return (
    <span data-testid={`pill-${status?.toLowerCase().replace(/\s/g, "-")}`}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />{statusLabel(status, isClient)}
    </span>
  );
}

function Avatars({ users }) {
  const shown = (users || []).slice(0, 3);
  const extra = (users || []).length - shown.length;
  return (
    <div className="flex -space-x-1.5">
      {shown.map((u, i) => (
        <span key={u.id || i} title={u.name} className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[9px] font-bold text-white" style={{ background: "#8B8FA3" }}>{initials(u.name)}</span>
      ))}
      {extra > 0 && <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[9px] font-bold text-slate-600">+{extra}</span>}
    </div>
  );
}

// ---- four stat tiles ----
export function StatTiles({ vals, isActive, onToggle }) {
  const items = [
    { id: "overdue", tint: "overdue", Icon: AlertTriangle, label: "Overdue", value: vals.overdue, sub: "Needs attention this week" },
    { id: "dueThisWeek", tint: "dueThisWeek", Icon: Clock, label: "Due this week", value: vals.dueThisWeek, sub: `Across ${vals.dueCats} categories` },
    { id: "upcoming", tint: "upcoming", Icon: Calendar, label: "Upcoming", value: vals.upcoming, sub: "No due date pressure yet" },
    { id: "completed", tint: "completed", Icon: CheckCircle2, label: "Completed", value: `${vals.completeCount} / ${vals.total}`, sub: `${vals.percent}% of request list`, noFilter: false },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-4">
      {items.map((it) => {
        const t = TILE_TINT[it.tint];
        const active = isActive(it.id);
        return (
          <button key={it.id} data-testid={`tile-${it.id}`} onClick={() => onToggle(it.id)}
            className={cn(card, "p-4 text-left transition-shadow hover:shadow-sm")}
            style={{ ...cardBorder, boxShadow: active ? `0 0 0 2px ${TOK.indigo}` : undefined }}>
            <div className="flex items-center justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-[10px]" style={{ background: t.bg }}><it.Icon className="h-[18px] w-[18px]" style={{ color: t.fg }} /></span>
              <span className="font-heading text-2xl font-extrabold" style={{ color: TOK.ink }}>{it.value}</span>
            </div>
            <div className="mt-3 text-sm font-semibold" style={{ color: TOK.ink }}>{it.label}</div>
            <div className="text-[11px]" style={{ color: TOK.inkFaint }}>{it.sub}</div>
          </button>
        );
      })}
    </div>
  );
}

// ---- engagement phases ----
export function PhaseCard({ phases, overall, isActive, onToggle }) {
  return (
    <div className={cn(card, "p-5 mb-4")} style={cardBorder}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold" style={{ color: TOK.ink }}>Engagement phases</span>
        <span className="text-xs font-semibold" style={{ color: TOK.inkSoft }}>Overall progress — {overall}%</span>
      </div>
      <div className="space-y-3">
        {phases.map((p, i) => {
          const active = isActive(p.id);
          return (
            <button key={p.id} data-testid={`phase-row-${p.id}`} onClick={() => onToggle(p.id)} className="flex w-full items-center gap-3 rounded-lg p-1 text-left" style={{ boxShadow: active ? `0 0 0 2px ${TOK.indigo}` : undefined }}>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold" style={{ background: TOK.track, color: TOK.indigo }}>{i + 1}</span>
              <span className="w-28 shrink-0 text-sm font-semibold" style={{ color: TOK.ink }}>{p.name}</span>
              <div className="h-2 flex-1 rounded-full" style={{ background: TOK.track }}>
                <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: TOK.indigo }} />
              </div>
              <span className="w-10 shrink-0 text-right text-xs font-semibold" style={{ color: TOK.inkSoft }}>{p.pct}%</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BarList({ title, topRight, rows, activeKey, onToggle, colorOf }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className={cn(card, "p-4")} style={cardBorder}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold" style={{ color: TOK.ink }}>{title}</span>
        <span className="text-[11px]" style={{ color: TOK.inkFaint }}>{topRight}</span>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => {
          const active = activeKey === r.key;
          return (
            <button key={r.key} data-testid={`bar-${r.key}`} onClick={() => onToggle(r.key)} className="block w-full text-left rounded p-0.5" style={{ boxShadow: active ? `0 0 0 2px ${TOK.indigo}` : undefined }}>
              <div className="flex items-center justify-between text-[12px] mb-1">
                <span className="font-semibold" style={{ color: TOK.inkSoft }}>{r.label}</span>
                <span className="font-bold" style={{ color: TOK.ink }}>{r.count}</span>
              </div>
              <div className="h-1.5 rounded-full" style={{ background: "#F1F2F7" }}>
                <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: colorOf(r.key) }} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function StatusChart({ counts, total, activeStatus, onToggle }) {
  const isClient = useIsClient();
  // Clients never see firm-side "Received" (unreleased) requests, so drop that row and relabel their view.
  const keys = isClient ? ["Awaiting Client", "Submitted", "Complete", "Overdue"]
                        : ["Received", "Awaiting Client", "Submitted", "Complete", "Overdue"];
  const rows = keys.map((s) => ({ key: s, label: statusLabel(s, isClient), count: counts[s] || 0 }));
  return <BarList title="Requests by status" topRight={`${total} total`} rows={rows} activeKey={activeStatus} onToggle={onToggle} colorOf={(k) => (STATUS[k] || STATUS.Received).dot} />;
}

export function DueChart({ counts, activeBucket, onToggle }) {
  const rows = ["overdue", "week", "next", "later"].map((k) => ({ key: k, label: DUE_LABEL[k], count: counts[k] || 0 }));
  return <BarList title="Requests by due date" topRight="next 30 days" rows={rows} activeKey={activeBucket} onToggle={onToggle} colorOf={(k) => DUE_COLOR[k]} />;
}

export function TeamCard({ members, activeAssignee, onToggle, canInvite, onInvite, onRemove }) {
  return (
    <div className={cn(card, "p-4")} style={cardBorder}>
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 text-sm font-bold" style={{ color: TOK.ink }}><Users className="h-4 w-4" style={{ color: TOK.indigo }} /> Engagement team</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px]" style={{ color: TOK.inkFaint }}>{members.length} members</span>
          {canInvite && <button data-testid="invite-client-btn" onClick={onInvite} className="text-slate-400 hover:text-indigo-600"><UserPlus className="h-4 w-4" /></button>}
        </div>
      </div>
      <div className="space-y-2.5">
        {members.length === 0 && <p className="text-sm" style={{ color: TOK.inkFaint }}>No team members yet.</p>}
        {members.map((m) => {
          const active = activeAssignee === m.id;
          return (
            <div key={m.id} data-testid={`team-row-${m.id}`} className="group flex items-center gap-2.5 rounded-lg p-1" style={{ boxShadow: active ? `0 0 0 2px ${TOK.indigo}` : undefined }}>
              <button onClick={() => onToggle(m.id)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                <span className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: m.external ? "#8B8FA3" : TOK.indigo }}>{initials(m.name)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold" style={{ color: TOK.ink }}>{m.name}</div>
                  <div className="text-[11px] capitalize" style={{ color: TOK.inkFaint }}>{(m.role || "").replace(/_/g, " ")}</div>
                </div>
              </button>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: TOK.track, color: TOK.indigo }}>{m.open}</span>
              {onRemove && (
                <button data-testid={`team-remove-${m.id}`} onClick={() => onRemove(m)} aria-label={`Remove ${m.name} from engagement`} title="Remove from engagement"
                  className="text-slate-300 opacity-40 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ActivityCard({ activity, onItemClick }) {
  return (
    <div className={cn(card, "p-4")} style={cardBorder}>
      <div className="flex items-center gap-2 mb-3"><ActivityIcon className="h-4 w-4" style={{ color: TOK.indigo }} /><span className="text-sm font-bold" style={{ color: TOK.ink }}>Activity</span></div>
      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
        {activity.length === 0 && <p className="text-sm" style={{ color: TOK.inkFaint }}>No recent activity.</p>}
        {activity.map((a) => (
          <button key={a.id} data-testid={`activity-${a.id}`} onClick={() => onItemClick && onItemClick(a)} className="flex w-full gap-2.5 text-left">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: TOK.indigo }} />
            <div className="min-w-0">
              <div className="text-[13px] leading-snug"><span className="font-bold" style={{ color: TOK.ink }}>{a.actor_name}</span> <span style={{ color: TOK.inkSoft }}>{a.action}</span></div>
              <div className="text-[11px]" style={{ color: TOK.inkFaint }}>{new Date(a.created_at).toLocaleString()}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DueBadge({ due }) {
  if (!due) return null;
  const overdue = new Date(due) < new Date(new Date().toDateString());
  return (
    <span className="rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap" style={{ background: overdue ? STATUS.Overdue.bg : "#F1F2F7", color: overdue ? STATUS.Overdue.fg : TOK.inkSoft }}>
      {new Date(due).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
    </span>
  );
}

function CategoryGroup({ name, items, canManage, onOpen, onAdd, onRename, selecting, selectedIds = [], onToggleSelect, onToggleCategory }) {
  const [open, setOpen] = useState(true);
  const ids = items.map((r) => r.id);
  const selCount = ids.filter((i) => selectedIds.includes(i)).length;
  const allSel = ids.length > 0 && selCount === ids.length;
  return (
    <div className="border-b last:border-0" style={{ borderColor: TOK.border }}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: TOK.categoryBg }}>
        {selecting && (
          <input type="checkbox" data-testid={`cat-select-${name}`} checked={allSel}
            ref={(el) => { if (el) el.indeterminate = selCount > 0 && !allSel; }}
            onChange={() => onToggleCategory(ids)} className="h-4 w-4 shrink-0 accent-indigo-600 cursor-pointer" />
        )}
        <button onClick={() => setOpen(!open)} data-testid={`cat-toggle-${name}`} className="text-slate-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
        <span className="text-[13px] font-bold" style={{ color: TOK.ink }}>{name}</span>
        <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold" style={{ background: TOK.track, color: TOK.indigo }}>{items.length}</span>
        {canManage && !selecting && (
          <div className="ml-auto flex items-center gap-1">
            <button data-testid={`cat-add-${name}`} onClick={() => onAdd(items[0]?.category_id)} className="rounded p-1 text-slate-400 hover:text-indigo-600"><Plus className="h-3.5 w-3.5" /></button>
            <button data-testid={`cat-rename-${name}`} onClick={onRename} className="rounded p-1 text-slate-400 hover:text-indigo-600"><Pencil className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      {open && items.map((r) => {
        const users = [...(r.internal_users || []), ...(r.client_users || [])];
        return (
          <div key={r.id} data-testid={`request-row-${r.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFD]" style={{ borderTop: `1px solid ${TOK.border}` }}>
            {selecting && (
              <input type="checkbox" data-testid={`select-req-${r.id}`} checked={selectedIds.includes(r.id)} onChange={() => onToggleSelect(r.id)} className="h-4 w-4 shrink-0 accent-indigo-600 cursor-pointer" />
            )}
            <button onClick={() => onOpen(r.id)} className="min-w-0 flex-1 text-left">
              <div className="text-[13px] font-semibold hover:underline" style={{ color: TOK.indigo }}>{r.name}</div>
              {r.description && <div className="truncate text-[11px]" style={{ color: TOK.inkFaint }}>{r.description}</div>}
            </button>
            <button onClick={() => onOpen(r.id)}><StatusPill status={r.status} /></button>
            <button onClick={() => onOpen(r.id)} className="hidden sm:block"><Avatars users={users} /></button>
            <button onClick={() => onOpen(r.id)}><DueBadge due={r.due_date} /></button>
            <button data-testid={`row-kebab-${r.id}`} onClick={() => onOpen(r.id)} className="rounded p-1 text-slate-400 hover:text-slate-700"><MoreVertical className="h-4 w-4" /></button>
          </div>
        );
      })}
    </div>
  );
}

const barSelect = "rounded-md border px-2 py-1 text-[11px] font-semibold bg-white disabled:opacity-40";
const barBtn = "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40";

export function RequestsPanel({ total, groups, pills, chips, onClearAll, onRemoveChip, canManage, onOpen, onAdd, onRename, onSubmitAll,
  selecting, selectedIds = [], onToggleSelect, onToggleCategory, onStartSelect, onCancelSelect, onSelectAllVisible, allVisibleIds = [],
  onBulkStatus, onBulkReassign, onBulkCategory, onBulkDelete, onBulkRestore, statuses = [], cats = [],
  deletedView = false, onToggleDeleted }) {
  const isClient = useIsClient();
  const n = selectedIds.length;
  const allVisSel = allVisibleIds.length > 0 && allVisibleIds.every((i) => selectedIds.includes(i));
  return (
    <div className={cn(card)} style={cardBorder}>
      {selecting ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: TOK.border, background: "#F1EFFE" }} data-testid="bulk-action-bar">
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-bold" style={{ color: TOK.ink }} data-testid="bulk-selected-count">{n} selected</span>
            <button data-testid="select-all-visible" onClick={onSelectAllVisible} className="text-[11px] font-semibold" style={{ color: TOK.indigo }}>{allVisSel ? "Clear all" : "Select all visible"}</button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {deletedView ? (
              <button data-testid="bulk-restore" disabled={n === 0} onClick={onBulkRestore} className={barBtn} style={{ background: "#E9F9F0", color: "#1E8E5A" }}><RotateCcw className="h-3 w-3" /> Restore</button>
            ) : (
              <>
                <select data-testid="bulk-status" disabled={n === 0} value="" className={barSelect} style={{ borderColor: TOK.border, color: TOK.inkSoft }}
                  onChange={(e) => { if (e.target.value) { onBulkStatus(e.target.value); e.target.value = ""; } }}>
                  <option value="">Change status…</option>
                  {statuses.map((s) => <option key={s} value={s}>{statusLabel(s, isClient)}</option>)}
                </select>
                <button data-testid="bulk-reassign" disabled={n === 0} onClick={onBulkReassign} className={barBtn} style={{ background: "#F1F2F7", color: TOK.inkSoft }}><Users className="h-3 w-3" /> Reassign</button>
                <select data-testid="bulk-category" disabled={n === 0} value="" className={barSelect} style={{ borderColor: TOK.border, color: TOK.inkSoft }}
                  onChange={(e) => { if (e.target.value) { onBulkCategory(e.target.value); e.target.value = ""; } }}>
                  <option value="">Move to category…</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button data-testid="bulk-delete" disabled={n === 0} onClick={onBulkDelete} className={barBtn} style={{ background: "#FDECEC", color: "#C0392B" }}><Trash2 className="h-3 w-3" /> Delete</button>
              </>
            )}
            <button data-testid="bulk-clear" onClick={onCancelSelect} className={barBtn} style={{ background: "transparent", color: TOK.inkSoft }}><X className="h-3 w-3" /> Clear selection</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: TOK.border }}>
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-bold" style={{ color: TOK.ink }}>Requests</span>
            <span className="text-[11px]" style={{ color: TOK.inkFaint }}>{total} total</span>
            {deletedView && <span data-testid="deleted-view-badge" className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#FDECEC", color: "#C0392B" }}>Deleted view</span>}
            {onSubmitAll && (
              <button data-testid="submit-all-to-client-btn" onClick={onSubmitAll}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold text-white"
                style={{ background: TOK.indigo }}>
                <Send className="h-3 w-3" /> Release to client
              </button>
            )}
            {canManage && groups.length > 0 && (
              <button data-testid="select-requests-btn" onClick={onStartSelect}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
                style={{ background: "#F1EFFE", color: TOK.indigo }}>
                <ListChecks className="h-3 w-3" /> Select
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {pills.map((p) => (
              <button key={p.key} data-testid={`pill-filter-${p.key}`} onClick={p.onClick}
                className={cn("rounded-full px-3 py-1 text-[11px] font-semibold")}
                style={p.active ? { background: TOK.indigo, color: "#fff" } : { background: "#F1F2F7", color: TOK.inkSoft }}>{p.label}</button>
            ))}
            {canManage && onToggleDeleted && (
              <button data-testid="toggle-deleted-btn" onClick={onToggleDeleted}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
                style={deletedView ? { background: TOK.indigo, color: "#fff" } : { background: "#F1F2F7", color: TOK.inkSoft }}>
                {deletedView ? "Active" : <><Trash2 className="h-3 w-3" /> Deleted</>}
              </button>
            )}
          </div>
        </div>
      )}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b" style={{ borderColor: TOK.border }}>
          {chips.map((c) => (
            <span key={c.id} data-testid={`chip-${c.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: TOK.track, color: TOK.indigo }}>
              {c.label}<button onClick={() => onRemoveChip(c)} className="ml-0.5">✕</button>
            </span>
          ))}
          <button data-testid="clear-all-filters" onClick={onClearAll} className="text-[11px] font-semibold" style={{ color: TOK.inkSoft }}>Clear all</button>
        </div>
      )}
      {groups.length === 0
        ? <div className="px-4 py-12 text-center text-sm" style={{ color: TOK.inkFaint }}>{deletedView ? "No deleted requests." : "No requests match the current filters."}</div>
        : groups.map((g) => <CategoryGroup key={g.name} name={g.name} items={g.items} canManage={canManage} onOpen={onOpen} onAdd={onAdd} onRename={onRename} selecting={selecting} selectedIds={selectedIds} onToggleSelect={onToggleSelect} onToggleCategory={onToggleCategory} />)}
    </div>
  );
}
